import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Bike, Zap, Square, Circle, Clock, Users, ChevronLeft, ChevronRight, 
  Check, CreditCard, Ticket, Star, Mountain, Disc, Car, CircleDot,
  Calendar, MapPin, Loader2, ServerOff, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useTranslations } from '../context/TranslationsContext';
import { useFitnet } from '../contexts/FitnetContext';
import { getHomePath } from '../utils/routes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Parse a YYYY-MM-DD string into a Date object at midnight LOCAL time.
 * This prevents the UTC interpretation that causes day shifts when
 * using new Date("YYYY-MM-DD") which treats it as UTC midnight.
 * 
 * Example: "2026-04-16" in Poland (UTC+2) should give April 16th, not April 15th 22:00
 */
const parseDateLocal = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

// Map Polish day names (from backend) to DB translation keys
const PL_DAY_TO_KEY = {
  'poniedziałek': 'monday', 'wtorek': 'tuesday', 'środa': 'wednesday',
  'czwartek': 'thursday', 'piątek': 'friday', 'sobota': 'saturday', 'niedziela': 'sunday'
};

// Month index (0-11) to DB key
const MONTH_KEYS_SHORT = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
const MONTH_KEYS_FULL = ['january','february','march','april','may_full','june','july','august','september','october','november','december'];

// Icon mapping for disciplines from FitNet
const disciplineIcons = {
  'bike': Bike,
  'zap': Zap,
  'square': Square,
  'circle': Circle,
  'circle-dot': CircleDot,
  'mountain': Mountain,
  'disc': Disc,
  'users': Users,
  'car': Car
};

// Icon mapping for class types based on code/name patterns
const getClassTypeIcon = (code, name) => {
  const codeLower = code.toLowerCase();
  const nameLower = name.toLowerCase();
  
  // Szkółka indywidualna
  if (codeLower.includes('indywidualn') || nameLower.includes('indywidualn')) {
    return { icon: Users, label: 'Indywidualne' };
  }
  // Szkółka grupowa
  if (codeLower.includes('grupow') || nameLower.includes('grupow')) {
    return { icon: Users, label: 'Grupowe' };
  }
  // Wejście jednorazowe
  if (codeLower.includes('jednorazowe') || nameLower.includes('jednorazowe')) {
    return { icon: Ticket, label: 'Jednorazowe' };
  }
  // Wejście całodniowe
  if (codeLower.includes('calodniowe') || codeLower.includes('całodniowe') || 
      nameLower.includes('calodniowe') || nameLower.includes('całodniowe')) {
    return { icon: Calendar, label: 'Całodniowe' };
  }
  // Nocne jazdki
  if (codeLower.includes('nocn') || nameLower.includes('nocn')) {
    return { icon: Star, label: 'Nocne' };
  }
  // Default
  return { icon: Clock, label: null };
};

// Sorting weight for class types
const getClassTypeSortWeight = (code, name) => {
  const codeLower = code.toLowerCase();
  const nameLower = name.toLowerCase();
  
  // Wejścia first
  if (codeLower.includes('jednorazowe') || nameLower.includes('jednorazowe')) return 1;
  if (codeLower.includes('calodniowe') || codeLower.includes('całodniowe')) return 2;
  // Then szkółki
  if (codeLower.includes('grupow') || nameLower.includes('grupow')) return 3;
  if (codeLower.includes('indywidualn') || nameLower.includes('indywidualn')) return 4;
  // Then special
  if (codeLower.includes('nocn') || nameLower.includes('nocn')) return 5;
  return 99;
};

// Sorting weight for time slots (by name priority, then by time)
const getSlotSortWeight = (slot) => {
  const nameLower = (slot.name || '').toLowerCase();
  
  // Wejścia jednorazowe first
  if (nameLower.includes('jednorazowe')) return 1;
  // Wejścia całodniowe second  
  if (nameLower.includes('całodniowe') || nameLower.includes('calodniowe')) return 2;
  // Szkółki grupowe
  if (nameLower.includes('grupow')) return 3;
  // Szkółki indywidualne
  if (nameLower.includes('indywidualn')) return 4;
  // Rest by time
  return 10;
};

const BookingPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td } = useTranslations();
  const { user, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const { disciplineSlug } = useParams();
  const lang = i18n.language;
  const { isAvailable: isFitnetAvailable, loading: fitnetLoading } = useFitnet();
  
  // Combined translation function
  const t = (key, fallback = null) => {
    const cmsValue = td(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const shortKey = key.split('.').pop();
    const cmsShort = td(shortKey);
    if (cmsShort && cmsShort !== shortKey) return cmsShort;
    return fallback || key;
  };

  // Translate day name from Polish backend value using DB translations
  const getDayShort = (plDayOfWeek) => {
    const key = PL_DAY_TO_KEY[plDayOfWeek?.toLowerCase()];
    if (!key) return plDayOfWeek?.slice(0, 3) || '';
    return td(`days.${key.slice(0, 3)}`) || plDayOfWeek.slice(0, 3);
  };

  // Translate month from Date object using DB translations  
  const getMonthShort = (date) => {
    const key = MONTH_KEYS_SHORT[date.getMonth()];
    return td(`months.${key}`) || date.toLocaleDateString('pl-PL', { month: 'short' });
  };

  // Full day name + date for summary (e.g. "Poniedziałek, 14 Kwietnia")
  const getFullDayName = (plDayOfWeek) => {
    const key = PL_DAY_TO_KEY[plDayOfWeek?.toLowerCase()];
    if (!key) return plDayOfWeek || '';
    return td(`days.${key}`) || plDayOfWeek;
  };

  const getMonthFull = (date) => {
    const key = MONTH_KEYS_FULL[date.getMonth()];
    return td(`months.${key}`) || date.toLocaleDateString('pl-PL', { month: 'long' });
  };

  const [step, setStep] = useState(1);
  const [disciplines, setDisciplines] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [subaccounts, setSubaccounts] = useState([]);
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [fitnetTranslations, setFitnetTranslations] = useState({});

  // Selection state
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [usePass, setUsePass] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  
  // System settings
  const [systemSettings, setSystemSettings] = useState({
    scheduleDaysAhead: 14,
    allowSameDayBooking: false,
    sameDayBookingCutoffHours: 2
  });
  
  // Loyalty points
  const [loyaltyPoints, setLoyaltyPoints] = useState({});
  const [loyaltyActive, setLoyaltyActive] = useState(false);

  // Helper function to normalize name to key (must match backend)
  const normalizeNameToKey = (name) => {
    if (!name) return '';
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ł/g, 'l').replace(/Ł/g, 'L')
      .replace(/[^a-zA-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  // Helper function to get translated name
  const getTranslatedName = (originalName) => {
    if (!originalName) return originalName;
    
    // Normalize the name to create the lookup key
    const key = normalizeNameToKey(originalName);
    
    // Look up translation
    if (fitnetTranslations[key]?.[lang]) {
      return fitnetTranslations[key][lang];
    }
    
    // Fallback: return original name
    return originalName;
  };

  // Validate promo code
  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    
    // Get product info for validation
    // IMPORTANT: Use code from FitNet, not normalized name - they must match admin panel
    const productName = selectedType?.name || selectedSlot?.name || '';
    const productId = selectedType?.code || selectedSlot?.classCode || selectedSlot?.code || normalizeNameToKey(productName);
    
    // Debug log for troubleshooting discount validation issues
    console.log('[Discount validation] Sending:', { 
      code: promoCode, 
      productId, 
      productName,
      selectedTypeCode: selectedType?.code,
      selectedSlotClassCode: selectedSlot?.classCode,
      selectedSlotCode: selectedSlot?.code
    });
    
    try {
      const response = await axios.post(`${API}/discount/validate`, {
        code: promoCode,
        price: selectedSlot?.price || 0,
        productType: 'session',
        productId: productId,
        productName: productName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.valid) {
        setAppliedPromo({
          code: response.data.code,
          discount: response.data.discountAmount,
          discountType: response.data.discountType,
          discountValue: response.data.discountValue,
          name: response.data.name
        });
        toast.success(`Zastosowano kod: ${response.data.code} (-${response.data.discountType === 'percentage' ? response.data.discountValue + '%' : response.data.discountAmount + ' zł'})`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || t('booking.invalidCode', 'Invalid code');
      toast.error(errorMsg);
      setAppliedPromo(null);
    }
  };

  // Fetch disciplines and translations on mount
  useEffect(() => {
    fetchDisciplines();
    fetchFitnetTranslations();
    fetchSystemSettings();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setSystemSettings(response.data);
      // Fetch loyalty points if program is active
      if (response.data.loyaltyProgramActive) {
        setLoyaltyActive(true);
        fetchLoyaltyPoints();
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    }
  };
  
  // Fetch loyalty points for products
  const fetchLoyaltyPoints = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/product-points`);
      if (response.data.active) {
        setLoyaltyActive(true);
        setLoyaltyPoints(response.data.points || {});
      } else {
        setLoyaltyActive(false);
        setLoyaltyPoints({});
      }
    } catch (error) {
      console.error('Failed to fetch loyalty points:', error);
    }
  };
  
  // Get loyalty points for a session/slot
  const getSessionLoyaltyPoints = (slot) => {
    if (!loyaltyActive || !slot) return 0;
    // Try normalized name key first (e.g., 'session_Wejście_jednorazowe')
    const nameKey = `session_${normalizeNameToKey(slot.name)}`;
    if (loyaltyPoints[nameKey]) return loyaltyPoints[nameKey];
    // Fallback to classCode or code
    const keys = [
      `session_${slot.classCode}`,
      `session_${slot.code}`
    ];
    for (const key of keys) {
      if (loyaltyPoints[key]) return loyaltyPoints[key];
    }
    return 0;
  };

  const fetchFitnetTranslations = async () => {
    try {
      const response = await axios.get(`${API}/admin/fitnet-translations`);
      setFitnetTranslations(response.data);
    } catch (error) {
      console.error('Failed to fetch translations:', error);
    }
  };

  // Fetch class types when discipline selected
  useEffect(() => {
    if (selectedDiscipline) {
      fetchClassTypes(selectedDiscipline.id);
      // Reset subsequent selections
      setSelectedType(null);
      setSelectedDay(null);
      setSelectedSlot(null);
      setAvailableDays([]);
      setAvailableSlots([]);
      // Reset promo code when discipline changes
      setAppliedPromo(null);
      setPromoCode('');
    }
  }, [selectedDiscipline]);

  // Fetch available days when class type selected
  useEffect(() => {
    if (selectedDiscipline && selectedType) {
      fetchAvailableDays();
      // Reset subsequent selections
      setSelectedDay(null);
      setSelectedSlot(null);
      setAvailableSlots([]);
      // Reset promo code when class type changes (it may not apply to new type)
      setAppliedPromo(null);
      setPromoCode('');
    }
  }, [selectedType]);

  // Fetch available slots when day selected
  useEffect(() => {
    if (selectedDiscipline && selectedType && selectedDay) {
      fetchAvailableSlots();
      setSelectedSlot(null);
    }
  }, [selectedDay]);

  // Fetch user data when reaching step 5
  useEffect(() => {
    if (isAuthenticated && step === 5) {
      fetchSubaccounts();
      fetchPasses();
    }
  }, [isAuthenticated, step]);

  const fetchDisciplines = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch disciplines and sort orders in parallel
      const [discResponse, sortResponse] = await Promise.all([
        axios.get(`${API}/disciplines`),
        axios.get(`${API}/sort-order/disciplines`).catch(() => ({ data: [] }))
      ]);
      
      // Create sort order map
      const orderMap = new Map(sortResponse.data.map(o => [o.itemId, o.order]));
      
      // Sort disciplines by custom order
      const sortedDisciplines = [...discResponse.data].sort((a, b) => {
        const orderA = orderMap.get(`disc_${a.id}`) ?? 999;
        const orderB = orderMap.get(`disc_${b.id}`) ?? 999;
        return orderA - orderB;
      });
      
      setDisciplines(sortedDisciplines);
      
      // Auto-select discipline from URL slug (matches translated slug for current language)
      if (disciplineSlug && !selectedDiscipline) {
        const match = sortedDisciplines.find(d => {
          // Match Polish slug directly
          if (d.slug === disciplineSlug) return true;
          // Match translated slug: get translated name, make slug from it
          const key = normalizeNameToKey(d.name);
          const translatedName = fitnetTranslations[key]?.[lang];
          if (translatedName) {
            const translatedSlug = translatedName.toLowerCase()
              .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
              .replace(/ł/g, 'l').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            if (translatedSlug === disciplineSlug) return true;
          }
          return false;
        });
        if (match) {
          setSelectedDiscipline(match);
          setStep(2);
        }
      }
    } catch (error) {
      console.error('Failed to fetch disciplines:', error);
      setError(t('booking.loadDisciplinesError', 'Failed to load disciplines'));
    } finally {
      setLoading(false);
    }
  };

  const fetchClassTypes = async (disciplineId) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch class types and sort orders in parallel
      const [response, sortResponse] = await Promise.all([
        axios.get(`${API}/disciplines/${disciplineId}/class-types`, {
          params: { includeInstructors: 'true' }
        }),
        axios.get(`${API}/sort-order/activityTypes`).catch(() => ({ data: [] }))
      ]);
      
      // Create sort order map
      const orderMap = new Map(sortResponse.data.map(o => [o.itemId, o.order]));
      
      // Sort class types by custom order (use code for matching, not id)
      const sortedClassTypes = [...response.data].sort((a, b) => {
        const keyA = `activity_${a.code}`;
        const keyB = `activity_${b.code}`;
        const orderA = orderMap.get(keyA) ?? 999;
        const orderB = orderMap.get(keyB) ?? 999;
        return orderA - orderB;
      });
      
      setClassTypes(sortedClassTypes);
    } catch (error) {
      console.error('Failed to fetch class types:', error);
      setError(t('booking.loadClassTypesError', 'Failed to load class types'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDays = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/disciplines/${selectedDiscipline.id}/available-days`, {
        params: { 
          classCode: selectedType.code,
          daysForward: systemSettings.scheduleDaysAhead || 14
        }
      });
      setAvailableDays(response.data);
    } catch (error) {
      console.error('Failed to fetch available days:', error);
      setError(t('booking.loadDaysError', 'Failed to load available days'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/disciplines/${selectedDiscipline.id}/available-slots`, {
        params: { 
          classCode: selectedType.code,
          date: selectedDay.date
        }
      });
      setAvailableSlots(response.data);
    } catch (error) {
      console.error('Failed to fetch available slots:', error);
      setError(t('booking.loadSlotsError', 'Failed to load available slots'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSubaccounts = async () => {
    try {
      const response = await axios.get(`${API}/subaccounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubaccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch subaccounts:', error);
    }
  };

  const fetchPasses = async () => {
    try {
      const response = await axios.get(`${API}/passes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPasses(response.data.filter(p => p.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch passes:', error);
    }
  };

  const handleConfirmBooking = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/book' } });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Step 1: Create booking
      const response = await axios.post(`${API}/bookings`, {
        groupId: selectedDiscipline.id,
        groupName: selectedDiscipline.name,
        typeCode: selectedType.code,
        typeName: selectedType.name,
        date: selectedDay.date,
        time: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        classId: selectedSlot.classId,
        serviceId: selectedSlot.serviceId,
        slotName: selectedSlot.name || selectedSlot.serviceName,
        price: selectedSlot.price,
        forUserId: selectedPerson || user.user_id,
        usePass,
        promoCode: appliedPromo?.code,
        instructor: selectedSlot.instructor || null, // Pass instructor info for SMS
        lang: lang // Pass user's language for email notifications
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const bookingId = response.data.booking_id;
      const finalPrice = response.data.price;
      
      // Step 2: If payment required, redirect to PayU
      if (finalPrice > 0 && !usePass) {
        console.log('Payment required, creating PayU order...', { bookingId, finalPrice });
        try {
          const paymentResponse = await axios.post(`${API}/payments/booking/${bookingId}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          console.log('Payment response:', paymentResponse.data);
          
          if (paymentResponse.data.success && paymentResponse.data.redirectUri) {
            // Redirect to PayU
            console.log('Redirecting to PayU:', paymentResponse.data.redirectUri);
            window.location.href = paymentResponse.data.redirectUri;
            return;
          } else {
            // Payment init failed - show booking success but warn about payment
            console.error('Payment init returned non-success:', paymentResponse.data);
            setBookingSuccess(true);
            setError(t('booking.paymentPendingManual', 'Booking created, but payment needs manual activation in "My Account".'));
          }
        } catch (paymentError) {
          console.error('Payment init failed:', paymentError.response?.data || paymentError.message);
          // Booking was created, just payment failed
          setBookingSuccess(true);
          setError(t('booking.paymentIncomplete', 'Booking created. You can complete payment in "My Account".'));
        }
      } else {
        // No payment required (using pass or free)
        setBookingSuccess(true);
      }
    } catch (error) {
      console.error('Booking failed:', error);
      const errorMessage = error.response?.data?.errorPl || error.response?.data?.error || 
        t('booking.bookingFailed', 'Booking failed. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: t('booking.discipline', 'Discipline') },
    { num: 2, label: t('booking.classType', 'Class Type') },
    { num: 3, label: t('booking.day', 'Day') },
    { num: 4, label: t('booking.time', 'Time') },
    { num: 5, label: t('booking.summary', 'Summary') }
  ];

  const canProceed = () => {
    switch (step) {
      case 1: return !!selectedDiscipline;
      case 2: return !!selectedType;
      case 3: return !!selectedDay;
      case 4: return !!selectedSlot;
      default: return true;
    }
  };

  const getPrice = () => {
    if (!selectedSlot) return 0;
    let price = selectedSlot.price || 0;
    if (appliedPromo) {
      price = Math.max(0, price - appliedPromo.discount);
    }
    if (usePass) return 0;
    return price;
  };

  const getIcon = (iconName, disciplineName = null) => {
    // Check if we have a custom icon from FitNet translations
    if (disciplineName && fitnetTranslations) {
      const key = normalizeNameToKey(disciplineName);
      const trans = fitnetTranslations[key];
      if (trans?._imageUrl) {
        return null; // Signal to use image instead
      }
      if (trans?._icon) {
        return disciplineIcons[trans._icon.toLowerCase()] || Circle;
      }
    }
    return disciplineIcons[iconName?.toLowerCase()] || Circle;
  };
  
  const getIconImage = (disciplineName) => {
    if (!disciplineName || !fitnetTranslations) return null;
    const key = normalizeNameToKey(disciplineName);
    const trans = fitnetTranslations[key];
    return trans?._imageUrl || null;
  };

  // Reset booking
  const resetBooking = () => {
    setBookingSuccess(false);
    setStep(1);
    setSelectedDiscipline(null);
    setSelectedType(null);
    setSelectedDay(null);
    setSelectedSlot(null);
    setSelectedPerson(null);
    setUsePass(false);
    setPromoCode('');
    setAppliedPromo(null);
  };

  // Show loading state while checking FitNet status
  if (fitnetLoading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">{t('common.loading', 'Ładowanie...')}</p>
        </div>
      </div>
    );
  }

  // Show unavailable message when FitNet is down
  if (!isFitnetAvailable) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg"
        >
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-orange-500/20 flex items-center justify-center">
              <ServerOff className="w-8 h-8 text-orange-400" />
            </div>
            <h1 className="text-2xl font-['Syne'] font-bold mb-4">{td('maintenance.bookingUnavailable', 'Rezerwacja sesji jest tymczasowo niedostępna')}</h1>
            <p className="text-muted-foreground mb-6">
              {td('maintenance.description', 'System rezerwacji jest chwilowo wyłączony. Przepraszamy za utrudnienia.')}
            </p>
            <Link 
              to={getHomePath(lang)} 
              className="btn-primary inline-block"
            >
              {td('maintenance.backToHome', 'Wróć do strony głównej')}
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success screen
  if (bookingSuccess) {
    return (
      <div className="min-h-screen pt-32 px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center glass-card p-12"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-4">
            {t('booking.bookingConfirmed', 'Booking confirmed!')}
          </h2>
          <p className="text-muted-foreground mb-8">
            {t('booking.bookingDetailsSent', 'Booking details have been sent to your email.')}
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/account')}
              className="btn-secondary"
              data-testid="booking-success-account"
            >
              {ti18n('nav.account')}
            </button>
            <button
              onClick={resetBooking}
              className="btn-primary"
              data-testid="booking-success-new"
            >
              {t('booking.newBooking', 'New booking')}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 sm:pt-32 pb-20 px-4 sm:px-6 md:px-12" data-testid="booking-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-['Syne'] font-bold mb-3 sm:mb-4">
            {t('booking.bookSession', 'Book a Session')}
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t('booking.bookInSteps', 'Book your session in a few steps')}
          </p>
        </div>

        {/* Progress Stepper */}
        <div className="mb-12">
          <div className="flex justify-between items-center relative">
            {/* Progress Line */}
            <div className="absolute top-4 left-0 right-0 h-0.5 bg-[var(--foreground)] opacity-10">
              <div 
                className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                style={{ width: `${((step - 1) / (steps.length - 1)) * 100}%` }}
              />
            </div>
            
            {steps.map((s) => (
              <div 
                key={s.num} 
                className={`stepper-step flex-1 ${step >= s.num ? 'active' : ''} ${step > s.num ? 'completed' : ''}`}
              >
                <div 
                  className={`stepper-circle w-8 h-8 text-sm font-bold rounded-full flex items-center justify-center relative z-10 transition-all ${
                    step > s.num ? 'bg-green-500 text-white' : step === s.num ? 'bg-primary text-white glow-primary' : 'bg-white/10 text-muted-foreground'
                  }`}
                >
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className={`mt-2 text-xs font-medium hidden sm:block ${step >= s.num ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-center">
            {error}
          </div>
        )}

        {/* Step Content */}
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="glass-card p-8"
        >
          {/* Step 1: Select Discipline */}
          {step === 1 && (
            <div data-testid="booking-step-1">
              <h2 className="text-2xl font-bold mb-6">
                {t('booking.selectDiscipline', 'Select Discipline')}
              </h2>
              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('common.loading', 'Loading...')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {disciplines.map((discipline) => {
                    const iconImage = getIconImage(discipline.name);
                    const Icon = iconImage ? null : getIcon(discipline.icon, discipline.name);
                    const isSelected = selectedDiscipline?.id === discipline.id;
                    return (
                      <button
                        key={discipline.id}
                        onClick={() => setSelectedDiscipline(discipline)}
                        className={`selection-card text-center p-6 rounded-xl border-2 transition-all hover:scale-105 ${
                          isSelected 
                            ? 'selected border-primary bg-primary/20 shadow-lg shadow-primary/30 ring-2 ring-primary/50' 
                            : 'border-white/10 dark:border-white/10 border-gray-200 bg-white/5 dark:bg-white/5 bg-gray-50 hover:border-white/20 dark:hover:border-white/20 hover:border-gray-300'
                        }`}
                        data-testid={`discipline-${discipline.slug}`}
                      >
                        {iconImage ? (
                          <img src={iconImage} alt={discipline.name} className="w-12 h-12 mx-auto mb-4 object-contain rounded" />
                        ) : (
                          <Icon className={`w-12 h-12 mx-auto mb-4 transition-colors ${isSelected ? 'text-primary' : 'text-gray-700 dark:text-white/70'}`} />
                        )}
                        <span className={`font-semibold transition-colors ${isSelected ? 'text-primary' : 'text-gray-800 dark:text-white'}`}>
                          {getTranslatedName(discipline.name)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Class Type */}
          {step === 2 && (
            <div data-testid="booking-step-2">
              <h2 className="text-2xl font-bold mb-6">
                {t('booking.selectClassType', 'Select Class Type')}
              </h2>
              {(() => {
                const isHulajnoga = selectedDiscipline?.name === 'HULAJNOGA';
                if (loading) {
                  return (
                    <div className="text-center py-12">
                      <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                      <p className="text-muted-foreground">{t('common.loading', 'Loading...')}</p>
                    </div>
                  );
                }
                if (classTypes.length === 0 && !isHulajnoga) {
                  return (
                    <div className="text-center py-12 text-muted-foreground">
                      {t('booking.noClassTypes', 'No class types available for this discipline')}
                    </div>
                  );
                }
                return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {classTypes.map((type) => {
                      const { icon: TypeIcon } = getClassTypeIcon(type.code, type.name);
                      const isSelected = selectedType?.code === type.code;
                      const instructor = type.instructor;
                      // Check for custom icon from admin
                      const classTypeIconImage = getIconImage(type.name);
                      
                      return (
                        <button
                          key={type.code}
                          onClick={() => setSelectedType(type)}
                          className={`selection-card flex items-center gap-4 p-5 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                            isSelected 
                              ? 'selected border-primary bg-primary/20 shadow-lg shadow-primary/30 ring-2 ring-primary/50' 
                              : 'border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 hover:border-gray-300 dark:hover:border-white/20'
                          }`}
                          data-testid={`class-type-${type.code}`}
                        >
                          {/* Instructor photo > custom image > icon */}
                          {instructor?.photo ? (
                            <div className={`w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-colors ${
                              isSelected ? 'border-primary' : 'border-gray-200 dark:border-white/20'
                            }`}>
                              <img 
                                src={instructor.photo} 
                                alt={instructor.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : classTypeIconImage ? (
                            <div className={`w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center transition-colors flex-shrink-0 ${
                              isSelected ? 'bg-primary/30' : 'bg-gray-100 dark:bg-white/10'
                            }`}>
                              <img src={classTypeIconImage} alt={type.name} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${
                              isSelected ? 'bg-primary/30' : 'bg-gray-100 dark:bg-white/10'
                            }`}>
                              <TypeIcon className={`w-6 h-6 transition-colors ${isSelected ? 'text-primary' : 'text-gray-700 dark:text-white/70'}`} />
                            </div>
                          )}
                          
                          {/* Name and instructor */}
                          <div className="text-left min-w-0">
                            <h3 className={`font-semibold text-md transition-colors break-words ${isSelected ? 'text-primary' : 'text-gray-800 dark:text-white'}`}>
                              {getTranslatedName(type.name)}
                            </h3>
                            {instructor && (
                              <p className="text-sm text-gray-500 dark:text-muted-foreground truncate">
                                {t('booking.instructor', 'Instructor')}: {instructor.fullName || instructor.name}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  {isHulajnoga && (
                    <a
                      href="https://www.szkolkizfliskiem.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="selection-card flex items-center gap-4 p-5 rounded-xl border-2 transition-all hover:scale-[1.02] border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 hover:border-gray-300 dark:hover:border-white/20 relative overflow-hidden"
                      data-testid="class-type-external-flisek"
                    >
                      <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border-2 border-gray-200 dark:border-white/20 bg-gradient-to-br from-amber-400/20 to-amber-600/10">
                        <img
                          src="/images/instructors/flisek.png"
                          alt="Paweł Flisek"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="text-left min-w-0 flex-1">
                        <h3 className="font-semibold text-md text-gray-800 dark:text-white break-words">
                          {t('booking.flisekTitle', 'Szkółka indywidualna hulajnoga - Paweł Flisek')}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-muted-foreground truncate">
                          {t('booking.instructor', 'Instructor')}: Paweł Flisek
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 dark:text-white/40 flex-shrink-0" />
                    </a>
                  )}
                </div>
                );
              })()}
            </div>
          )}

          {/* Step 3: Select Day */}
          {step === 3 && (
            <div data-testid="booking-step-3">
              <h2 className="text-2xl font-bold mb-6">
                {t('booking.selectDay', 'Select Day')}
              </h2>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">{t('common.loading', 'Loading...')}</p>
                </div>
              ) : availableDays.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  {t('booking.noDaysAvailable', 'No available days for selected class type')}
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 gap-3">
                  {availableDays
                    .filter((day) => {
                      // Filter out past dates - don't show them at all
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      // Use parseDateLocal to avoid UTC timezone shift
                      const dayDate = parseDateLocal(day.date);
                      return dayDate.getTime() >= today.getTime();
                    })
                    .map((day) => {
                    // Use parseDateLocal to avoid UTC timezone shift
                    const date = parseDateLocal(day.date);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    
                    // Check if this is today
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    // Use parseDateLocal to avoid UTC timezone shift
                    const dayDate = parseDateLocal(day.date);
                    const isToday = dayDate.getTime() === today.getTime();
                    
                    // Block same-day booking if not allowed
                    const isTodayBlocked = isToday && !systemSettings.allowSameDayBooking;
                    
                    if (isTodayBlocked) {
                      return (
                        <div
                          key={day.date}
                          className="text-center py-4 px-2 rounded-xl border-2 border-white/5 bg-white/5 opacity-50 cursor-not-allowed"
                          data-testid={`day-${day.date}-blocked`}
                        >
                          <div className="text-xs mb-1 capitalize text-muted-foreground">
                            {getDayShort(day.dayOfWeek)}
                          </div>
                          <div className="text-xl font-bold text-muted-foreground">{date.getDate()}</div>
                          <div className="text-xs text-muted-foreground">
                            {getMonthShort(date)}
                          </div>
                          <div className="text-[10px] text-red-400 mt-1">
                            {t('booking.timeExpired', 'Time expired')}
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <button
                        key={day.date}
                        onClick={() => {
                          setSelectedDay(day);
                          // Scroll to top on mobile after selecting day
                          if (window.innerWidth < 768) {
                            setTimeout(() => {
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }, 100);
                          }
                        }}
                        className={`text-center py-4 px-2 rounded-xl border-2 transition-all hover:scale-105 ${
                          selectedDay?.date === day.date 
                            ? 'border-primary bg-primary/10 shadow-lg shadow-primary/20' 
                            : isWeekend 
                              ? 'border-orange-500/30 bg-orange-500/5 hover:border-orange-500/50' 
                              : 'border-white/10 bg-white/5 hover:border-white/20'
                        }`}
                        data-testid={`day-${day.date}`}
                      >
                        <div className={`text-xs mb-1 capitalize ${isWeekend ? 'text-orange-400' : 'text-muted-foreground'}`}>
                          {getDayShort(day.dayOfWeek)}
                        </div>
                        <div className="text-xl font-bold">{date.getDate()}</div>
                        <div className="text-xs text-muted-foreground">
                          {getMonthShort(date)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Select Time Slot */}
          {step === 4 && (
            <div data-testid="booking-step-4">
              <h2 className="text-2xl font-bold mb-6">
                {t('booking.selectTime', 'Select Time')}
              </h2>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">{t('common.loading', 'Loading...')}</p>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  {t('booking.noTimesAvailable', 'No available slots for selected day')}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[...availableSlots]
                    .sort((a, b) => {
                      // First sort by name weight
                      const weightA = getSlotSortWeight(a);
                      const weightB = getSlotSortWeight(b);
                      if (weightA !== weightB) return weightA - weightB;
                      // Then by start time
                      return a.startTime.localeCompare(b.startTime);
                    })
                    .map((slot, index) => {
                    const isActive = slot.active && slot.freeSpots > 0;
                    const isSelected = selectedSlot?.classId === slot.classId && selectedSlot?.serviceId === slot.serviceId;
                    return (
                      <button
                        key={`${slot.classId}-${slot.serviceId}-${index}`}
                        onClick={() => isActive && setSelectedSlot(slot)}
                        disabled={!isActive}
                        className={`selection-card relative p-5 rounded-xl border-2 transition-all text-left ${
                          isSelected
                            ? 'selected border-primary bg-primary/20 shadow-lg shadow-primary/30 ring-2 ring-primary/50' 
                            : isActive 
                              ? 'border-white/10 bg-white/5 hover:border-white/20 hover:scale-[1.02]' 
                              : 'border-white/5 bg-white/5 opacity-50 cursor-not-allowed'
                        }`}
                        data-testid={`slot-${slot.startTime.replace(':', '')}`}
                      >
                        {/* Time and Name */}
                        <div className="flex justify-between items-start mb-3 pr-12">
                          <div>
                            <div className={`text-xl font-bold flex items-center gap-2 transition-colors ${isSelected ? 'text-primary' : ''}`}>
                              <Clock className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-primary'}`} />
                              {slot.startTime} - {slot.endTime}
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                              {getTranslatedName(slot.name)}
                            </div>
                          </div>
                        </div>
                        
                        {/* Details */}
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          {/* Duration */}
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                            {slot.durationFormatted}
                          </span>
                          
                          {/* Price */}
                          {slot.price && (
                            <span className="flex items-center gap-1 font-semibold text-primary">
                              {slot.priceFormatted}
                            </span>
                          )}
                          
                          {/* Loyalty points badge */}
                          {loyaltyActive && getSessionLoyaltyPoints(slot) > 0 && (
                            <span className="flex items-center gap-1 text-yellow-500">
                              <Star className="w-3.5 h-3.5" />
                              <span className="text-xs font-medium">
                                +{getSessionLoyaltyPoints(slot)} {t('loyalty.points', 'pkt')}
                              </span>
                            </span>
                          )}
                          
                          {/* Availability - only show if there's actually a limit (freeSpots < 1000) */}
                          {!isActive ? (
                            <span className="flex items-center gap-1 text-red-400">
                              <span className="w-2 h-2 rounded-full bg-red-400"></span>
                              {t('booking.full', 'Full')}
                            </span>
                          ) : slot.freeSpots < 1000 ? (
                            <span className="flex items-center gap-1 text-green-400">
                              <span className="w-2 h-2 rounded-full bg-green-400"></span>
                              {slot.freeSpots} {t('booking.spotsAvailable', 'spots')}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-green-400">
                              <span className="w-2 h-2 rounded-full bg-green-400"></span>
                              {t('booking.available', 'Available')}
                            </span>
                          )}
                        </div>
                        
                        {/* Schedule info 
                        {slot.schedule && (
                          <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {slot.schedule.name}
                          </div>
                        )}
                        */}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 5: Summary */}
          {step === 5 && (
            <div data-testid="booking-step-5">
              <h2 className="text-2xl font-bold mb-6">
                {t('booking.summary', 'Summary')}
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Booking Details */}
                <div className="space-y-4">
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">
                      {t('booking.discipline', 'Discipline')}
                    </p>
                    <p className="font-semibold flex items-center gap-2">
                      {selectedDiscipline && (() => {
                        const Icon = getIcon(selectedDiscipline.icon);
                        return <Icon className="w-5 h-5 text-primary" />;
                      })()}
                      {selectedDiscipline?.name}
                    </p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">
                      {t('booking.classType', 'Class Type')}
                    </p>
                    <p className="font-semibold">{selectedType?.name}</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">
                      {t('booking.date', 'Date')}
                    </p>
                    <p className="font-semibold">
                      {selectedDay && (() => {
                        const date = parseDateLocal(selectedDay.date);
                        return `${getFullDayName(selectedDay.dayOfWeek)}, ${date.getDate()} ${getMonthFull(date)}`;
                      })()}
                    </p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">
                      {t('booking.time', 'Time')}
                    </p>
                    <p className="font-semibold">
                      {selectedSlot?.startTime} - {selectedSlot?.endTime}
                      <span className="text-muted-foreground text-sm ml-2">
                        ({selectedSlot?.durationFormatted})
                      </span>
                    </p>
                  </div>
                </div>

                {/* Payment Options */}
                <div className="space-y-4">
                  {isAuthenticated && (
                    <>
                      {/* Select Person */}
                      <div className="p-4 bg-white/5 rounded-xl">
                        <p className="text-sm text-muted-foreground mb-3">
                          {t('booking.forWhom', 'For whom?')}
                        </p>
                        <div className="space-y-2">
                          <button
                            onClick={() => setSelectedPerson(null)}
                            className={`w-full text-left p-3 rounded-lg border transition-colors ${
                              !selectedPerson ? 'border-primary bg-primary/10' : 'border-white/10 hover:border-white/20'
                            }`}
                            data-testid="select-person-me"
                          >
                            {user?.firstName} {user?.lastName} ({t('account.me', 'Me')})
                          </button>
                          {subaccounts.map((sub) => (
                            <button
                              key={sub.subaccount_id}
                              onClick={() => setSelectedPerson(sub.subaccount_id)}
                              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                                selectedPerson === sub.subaccount_id ? 'border-primary bg-primary/10' : 'border-white/10 hover:border-white/20'
                              }`}
                              data-testid={`select-person-${sub.subaccount_id}`}
                            >
                              {sub.firstName} {sub.lastName}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Use Pass */}
                      {passes.length > 0 && (
                        <div className="p-4 bg-white/5 rounded-xl">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={usePass}
                              onChange={(e) => setUsePass(e.target.checked)}
                              className="w-5 h-5 rounded border-white/20 bg-white/5 text-primary focus:ring-primary"
                              data-testid="use-pass-checkbox"
                            />
                            <span className="flex items-center gap-2">
                              <Ticket className="w-5 h-5 text-primary" />
                              {t('booking.usePass', 'Use pass')}
                            </span>
                          </label>
                        </div>
                      )}
                    </>
                  )}

                  {/* Promo Code */}
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-3">
                      {t('booking.discountCode', 'Promo Code')}
                    </p>
                    <div className="grid gap-2 lg:flex">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="PROMO123"
                        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 focus:outline-none focus:border-primary"
                        data-testid="promo-code-input"
                      />
                      <button
                        onClick={validatePromoCode}
                        disabled={!promoCode.trim()}
                        className="btn-secondary px-6 disabled:opacity-50"
                        data-testid="apply-promo-button"
                      >
                        {t('booking.apply', 'Apply')}
                      </button>
                    </div>
                    
                    {/* Promo code success */}
                    {appliedPromo && (
                      <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg" data-testid="promo-code-success">
                        <p className="text-green-500 text-sm flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          {t('booking.codeApplied', 'Kod zastosowany!')}
                          <span className="font-semibold">
                            {appliedPromo.discountType === 'percentage' 
                              ? `-${appliedPromo.discountValue}%`
                              : `-${appliedPromo.discount?.toFixed(2) || appliedPromo.discountValue} zł`
                            }
                          </span>
                        </p>
                        {getPrice() === 0 && (
                          <p className="text-green-400 text-xs mt-1">
                            {t('booking.fullDiscountInfo', '100% rabatu - rezerwacja będzie bezpłatna!')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Total */}
                  <div className="p-6 glass-card relative">
                    <div className="flex justify-between items-center text-lg">
                      <span>{t('booking.toPay', 'Do zapłaty')}:</span>
                      <div className="text-right">
                        {appliedPromo ? (
                          <div className="flex items-center gap-3">
                            <span className="text-lg text-white/50 line-through">
                              {(selectedSlot?.price || 0).toFixed(2)} zł
                            </span>
                            <span className="text-2xl font-bold text-green-400">
                              {getPrice().toFixed(2)} zł
                            </span>
                          </div>
                        ) : (
                          <span className="text-2xl font-bold text-primary">
                            {getPrice().toFixed(2)} zł
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Loyalty points info in summary */}
                    {loyaltyActive && getSessionLoyaltyPoints(selectedSlot) > 0 && !usePass && (
                      <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2 text-yellow-500">
                        <Star className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {t('loyalty.forBookingYouGet', 'Za tę rezerwację otrzymasz')} +{getSessionLoyaltyPoints(selectedSlot)} {t('loyalty.pointsEarned', 'punktów lojalnościowych')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-4 mt-6 sm:mt-8">
          <button
            onClick={() => setStep(step - 1)}
            disabled={step === 1}
            className={`btn-secondary flex items-center justify-center gap-2 py-3 sm:py-2 text-sm sm:text-base ${step === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="booking-back-button"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" /> {t('common.back', 'Back')}
          </button>

          {step < 5 ? (
            <button
              onClick={() => {
                setStep(step + 1);
                // Scroll to top on mobile when going to next step
                if (window.innerWidth < 768) {
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }, 100);
                }
              }}
              disabled={!canProceed()}
              className={`btn-primary flex items-center justify-center gap-2 py-3 sm:py-2 text-sm sm:text-base ${!canProceed() ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="booking-next-button"
            >
              {t('common.next', 'Next')} <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          ) : (
            <button
              onClick={handleConfirmBooking}
              disabled={loading}
              className="btn-primary flex items-center justify-center gap-2 py-3 sm:py-2 text-sm sm:text-base"
              data-testid="booking-confirm-button"
            >
              {loading ? (
                <span>{t('common.loading', 'Loading...')}</span>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                  {isAuthenticated 
                    ? t('booking.confirmBooking', 'Confirm Booking')
                    : t('auth.login', 'Login')
                  }
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
